import { useEffect, useMemo, useState } from 'react';
import { usePayComContext } from '@/lib/paycom/context';
import type { SavedCardInstrument } from '@/lib/paycom/types';
import { WithdrawalCardPicker } from './WithdrawalCardPicker';
import { WithdrawalCardSetup } from './WithdrawalCardSetup';

interface Props {
  /**
   * Notifies the parent whenever the user picks a different card or saves
   * a newly-tokenized one. Not called on initial mount — the parent treats
   * its own absent selection as "nothing chosen yet" without needing a
   * `null` sync from the panel.
   */
  onSelectionChange: (card: SavedCardInstrument | null) => void;
  /**
   * Mode controlled by the parent so the form layout can collapse to a
   * full-screen "add card" view. The parent decides which UI to render
   * around the panel; the panel renders the matching content.
   */
  mode: 'pick' | 'setup';
  onRequestAddNew: () => void;
  onCancelAddNew: () => void;
}

/**
 * Owns card-mode state for the withdraw flow: the currently-selected card,
 * any locally-added cards (saved within this session) merged with the
 * PayCom-vault list, and the bridge between the "pick" and "setup" sub-views.
 */
export function CardWithdrawPanel({ onSelectionChange, mode, onRequestAddNew, onCancelAddNew }: Props) {
  const { state: payCom } = usePayComContext();

  // The user's explicit pick (or deselect). While unset, the selection
  // DERIVES to the default (first) vault card so the method screen opens
  // ready to confirm (ARC-150) — no setState-in-effect needed.
  const [explicitPick, setExplicitPick] = useState<{ card: SavedCardInstrument | null } | null>(null);
  const [localCards, setLocalCards] = useState<SavedCardInstrument[]>([]);

  // Merge locally-saved (this-session) cards with the vault list. Local
  // wins on dedupe so a card just tokenized appears at the top until the
  // vault refresh catches up.
  const mergedCards = useMemo<SavedCardInstrument[]>(() => {
    const all = [...localCards, ...payCom.savedCards];
    const seen = new Set<string>();
    return all.filter((c) => (seen.has(c.id) ? false : (seen.add(c.id), true)));
  }, [localCards, payCom.savedCards]);

  const selectedCard = explicitPick ? explicitPick.card : (mergedCards[0] ?? null);

  // Keep the parent's mirror (used by the submit payload) in sync with the
  // derived selection — including the auto-selected default.
  useEffect(() => {
    onSelectionChange(selectedCard);
  }, [selectedCard, onSelectionChange]);

  function handleSelect(card: SavedCardInstrument | null) {
    setExplicitPick({ card });
  }

  function handleSaved(card: SavedCardInstrument) {
    setLocalCards((prev) => (prev.some((c) => c.id === card.id) ? prev : [card, ...prev]));
    setExplicitPick({ card });
    onCancelAddNew();
  }

  function handleAddNew() {
    onRequestAddNew();
  }

  if (mode === 'setup') {
    return <WithdrawalCardSetup onSaved={handleSaved} onCancel={onCancelAddNew} />;
  }

  return (
    <WithdrawalCardPicker
      cards={mergedCards}
      selectedId={selectedCard?.id ?? null}
      onSelect={handleSelect}
      onAddNew={handleAddNew}
    />
  );
}
